/**
 * Issue #324 — AI 评分规则的「前端显示区间」推导（纯函数，可单测）。
 *
 * 仅存三条下界 qualifiedMin/goodMin/excellentMin；不合格自动为 < qualifiedMin。
 * 这里把三条下界换算成四档闭区间文案（百分制 0-100），供「系统设置 → AI评分规则」卡片
 * 展示，让管理员直观看到每档分数区间。阈值口径（PASS/FAIL、等级文案）由后端单一来源，
 * 前端只负责把同一份三下界换算成可读区间，不重复实现判级逻辑。
 */
import type { QcScoringRulesConfig } from './types';

export interface QcGradeInterval {
  grade: string;
  /** 闭区间文案，例如 "60-69"；空档显示「无」。 */
  range: string;
}

const SCORE_MAX = 100;

/** 把闭区间 [lo, hiExclusive-1] 渲染为文案；空档返回「无」，单点返回单值。 */
function closedBand(lo: number, hiExclusive: number): string {
  const hi = hiExclusive - 1;
  if (lo > hi) return '无';
  if (lo === hi) return `${lo}`;
  return `${lo}-${hi}`;
}

/**
 * 四档显示区间（与后端 qcScoreGradeLabel 同口径）：
 *   不合格 [0, qualifiedMin)、合格 [qualifiedMin, goodMin)、
 *   良好 [goodMin, excellentMin)、优秀 [excellentMin, 100]。
 */
export function qcScoringRuleIntervals(
  rules: QcScoringRulesConfig,
): QcGradeInterval[] {
  const { qualifiedMin, goodMin, excellentMin } = rules;
  return [
    { grade: '不合格', range: qualifiedMin <= 0 ? '无' : `0-${qualifiedMin - 1}` },
    { grade: '合格', range: closedBand(qualifiedMin, goodMin) },
    { grade: '良好', range: closedBand(goodMin, excellentMin) },
    {
      grade: '优秀',
      range:
        excellentMin > SCORE_MAX
          ? '无'
          : excellentMin === SCORE_MAX
            ? `${SCORE_MAX}`
            : `${excellentMin}-${SCORE_MAX}`,
    },
  ];
}

/** 校验三下界顺序合法（区间 [0,100] 且 qualifiedMin <= goodMin <= excellentMin）。前端预校验，后端兜底。 */
export function isQcScoringRulesOrderValid(rules: QcScoringRulesConfig): boolean {
  const bounds = [rules.qualifiedMin, rules.goodMin, rules.excellentMin];
  if (bounds.some((v) => !Number.isInteger(v) || v < 0 || v > SCORE_MAX)) {
    return false;
  }
  return rules.qualifiedMin <= rules.goodMin && rules.goodMin <= rules.excellentMin;
}
